import jsep from "jsep";

/** Integer cents. Never a float of dollars. */
export type Cents = number;

const currency = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
});

/** `-1234` → `−$12.34` (uses a true minus sign so columns line up). */
export function formatCents(cents: Cents, options: { signed?: boolean } = {}): string {
  const text = currency.format(Math.abs(cents) / 100);
  if (cents < 0) {
    return `−${text}`;
  }
  if (options.signed && cents > 0) {
    return `+${text}`;
  }
  return text;
}

/** `1234` → `12.34`, for editable fields. */
export function centsToPlain(cents: Cents): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

/** Parses a decimal money string such as `-$1,234.5` or `(12.00)` into cents. */
export function parseMoney(text: string): Cents | null {
  let s = text
    .trim()
    .replace(/[\s$,]/g, "")
    .replace(/−/g, "-");
  let negative = false;
  const paren = /^\((.*)\)$/.exec(s);
  if (paren) {
    negative = true;
    s = paren[1] ?? "";
  }
  if (s.startsWith("-")) {
    negative = !negative;
    s = s.slice(1);
  } else if (s.startsWith("+")) {
    s = s.slice(1);
  }
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  if (!m || (m[1] === "" && (m[2] ?? "") === "")) {
    return null;
  }
  const cents = Number(m[1] || "0") * 100 + Number((m[2] ?? "").padEnd(2, "0") || "0");
  return negative ? -cents : cents;
}

const binaryOps = new Set(["+", "-", "*", "/"]);

/**
 * Evaluates an amount field: `120+45`, `-20`, or `*2` / `+10` applied to the current value.
 * Only + − × ÷ on numbers are allowed. Returns cents, or null if invalid.
 */
export function evaluateAmountExpression(text: string, current: Cents = 0): Cents | null {
  let expr = text
    .trim()
    .replace(/[$,\s]/g, "")
    .replace(/−/g, "-")
    .replace(/[×x]/g, "*")
    .replace(/÷/g, "/");
  if (expr === "") {
    return null;
  }
  if (/^[*/]/.test(expr) || (/^\+/.test(expr) && expr.length > 1)) {
    expr = `${centsToPlain(current)}${expr}`;
  }
  let tree: jsep.Expression;
  try {
    tree = jsep(expr);
  } catch {
    return null;
  }
  const value = evaluate(tree);
  if (value === null || !Number.isFinite(value)) {
    return null;
  }
  return Math.round(value * 100);
}

function evaluate(node: jsep.Expression): number | null {
  if (node.type === "Literal") {
    return typeof node.value === "number" ? node.value : null;
  }
  if (node.type === "UnaryExpression") {
    const u = node as jsep.UnaryExpression;
    const arg = evaluate(u.argument);
    if (arg === null) {
      return null;
    }
    if (u.operator === "-") {
      return -arg;
    }
    return u.operator === "+" ? arg : null;
  }
  if (node.type === "BinaryExpression") {
    const b = node as jsep.BinaryExpression;
    if (!binaryOps.has(b.operator)) {
      return null;
    }
    const left = evaluate(b.left);
    const right = evaluate(b.right);
    if (left === null || right === null) {
      return null;
    }
    switch (b.operator) {
      case "+":
        return left + right;
      case "-":
        return left - right;
      case "*":
        return left * right;
      default:
        return right === 0 ? null : left / right;
    }
  }
  return null;
}

export function sumCents(values: Iterable<Cents>): Cents {
  let total = 0;
  for (const v of values) {
    total += v;
  }
  return total;
}
